// THE RARER FILTERS (UI rebuild batch 2) — what sits behind Money movements' one Filters button: Type, Asset, Party,
// Account and Show reversed. The controls are today's (FilterSelect, Combobox with its readFailed line, the reversed
// chip); the screen owns their state, as it did. Search and the period stay on the bar in front.
import type { ReactNode } from 'react';
import { Chip } from '../../FilterRail';
import { FilterSelect } from '../../FilterBar';
import { Combobox, type ComboboxOption } from '../../Combobox';
import type { MovementKindFilter } from '../../../lib/api';
import { movementKindLabel } from '../../../lib/naming';

/** The two name pickers' box: the PHONE FIELD size below lg (iOS zooms the page under 16px), body from lg. */
const COMBO = 'w-full [&_input]:text-field lg:[&_input]:text-body';

/** `wide`: a name picker takes the phone's whole line (two selects share one). */
function Field({ label, wide = false, children }: { label: string; wide?: boolean; children: ReactNode }) {
  return (
    <div className={`min-w-0 ${wide ? 'col-span-2 lg:col-span-1' : ''}`}>
      <div className="mb-1 text-label font-medium uppercase tracking-wider text-tg-hint">{label}</div>
      {children}
    </div>
  );
}

export function MovementFilters({
  kinds, kind, setKind, assetOptions, asset, setAsset,
  partyTyped, setPartyTyped, partyOptions, partiesLoading, partiesFailed, partyNamed,
  railTyped, setRailTyped, railOptions, railsLoading, railsFailed,
  undone, setUndone, onClear, filtered,
}: {
  kinds: MovementKindFilter[];
  kind: MovementKindFilter;
  setKind: (k: MovementKindFilter) => void;
  assetOptions: ReadonlyArray<{ value: string; label: string }>;
  asset: string;
  setAsset: (a: string) => void;
  partyTyped: string;
  setPartyTyped: (v: string) => void;
  partyOptions: ComboboxOption[];
  partiesLoading: boolean;
  partiesFailed?: ReactNode;
  /** Who the party filter names, once the books answered — so a filter cannot point at the wrong counterparty silently. */
  partyNamed?: ReactNode;
  railTyped: string;
  setRailTyped: (v: string) => void;
  railOptions: ComboboxOption[];
  railsLoading: boolean;
  railsFailed?: ReactNode;
  undone: boolean;
  setUndone: (fn: (v: boolean) => boolean) => void;
  onClear: () => void;
  filtered: boolean;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-4">
        <Field label="Type">
          <FilterSelect
            value={kind}
            onChange={setKind}
            allValue="all"
            className="lg:w-full"
            options={kinds.filter((k) => k !== 'all').map((k) => ({ value: k, label: movementKindLabel(k) }))}
          />
        </Field>
        <Field label="Asset">
          <FilterSelect value={asset} onChange={setAsset} allValue="" className="lg:w-full" options={assetOptions} />
        </Field>
        <Field label="Party" wide>
          <div className={COMBO}>
            <Combobox
              value={partyTyped}
              onChange={setPartyTyped}
              options={partyOptions}
              loading={partiesLoading}
              placeholder="any party"
              emptyHint="No active party by that name — type an archived one in full."
              readFailed={partiesFailed}
            />
          </div>
          {partyNamed && <div className="mt-1 break-words text-meta text-tg-hint">{partyNamed}</div>}
        </Field>
        <Field label="Account" wide>
          <div className={COMBO}>
            <Combobox
              value={railTyped}
              onChange={setRailTyped}
              options={railOptions}
              loading={railsLoading}
              placeholder="any account"
              emptyHint="No account by that name."
              readFailed={railsFailed}
            />
          </div>
        </Field>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Chip active={undone} onClick={() => setUndone((v) => !v)}>Show reversed</Chip>
        {filtered && <button type="button" onClick={onClear} className="btn-link text-body">Clear filters</button>}
      </div>
    </div>
  );
}
